/**
 * v2.71.99 — Admin User Profile page.
 *
 * Route: /admin/users/:id
 *
 * Single-user pivot view used when admin clicks a row in "Recent Users"
 * or the Users tab. Solves two user-spec issues at once:
 *   • ISSUE 3 — Recent Users not clickable → now navigates here.
 *   • ISSUE 6 — User property visibility missing → table + stat cards on
 *     this same page, with inline Approve / Reject / View / Edit / Delete.
 *
 * Tabs
 * ────
 *   1. Overview      — identity, contact, role, join date, stat cards
 *   2. Listings      — table of the user's properties with status filter
 *                       + admin row-level actions
 *   3. Activity Log  — chronological auto_leads ledger
 *
 * Data sources (all NEW admin-scoped endpoints added in v2.71.99):
 *   • GET /api/admin/users/:id/profile       → { user, stats }
 *   • GET /api/admin/users/:id/properties    → property list (filterable)
 *   • GET /api/admin/users/:id/activity      → auto_leads ledger
 *
 * Note: this file uses canonical data-fetch-on-mount React patterns.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, Calendar, Shield, User as UserIcon, Building2, ListChecks,
  CheckCircle2, Clock, Ban, Eye, Pencil, Trash2, RefreshCw, Activity, MapPin, IndianRupee,
} from "lucide-react";
import api from "../lib/api";
import { formatINR } from "../shared/services/api";
import { DashboardLayout } from "../features/dashboard";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
// v2.80 — Refresh stats + listings + activity when admin or the user
// themselves mutate from another tab.
import { useDataSync } from "../lib/dataSync";

/* ─── helpers ──────────────────────────────────────────────────────── */

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

const STATUS_BUCKETS = [
  { key: "all",      label: "All",      testid: "user-prop-tab-all" },
  { key: "active",   label: "Active",   testid: "user-prop-tab-active" },
  { key: "pending",  label: "Pending",  testid: "user-prop-tab-pending" },
  { key: "sold",     label: "Sold",     testid: "user-prop-tab-sold" },
  { key: "rejected", label: "Rejected", testid: "user-prop-tab-rejected" },
];

/* ─── main component ───────────────────────────────────────────────── */

export default function AdminUserProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${id}/profile`);
      setData(r.data);
    } catch (e) {
      toast.error("Couldn't load user profile");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id && isAdmin) refresh();
  }, [id, isAdmin, refresh]);

  // v2.80 — Re-pull profile + listings stats when this user (or a
  // property they own) mutates anywhere in the system.
  useDataSync(["users", "properties"], (_domains, payload) => {
    if (!isAdmin || !id) return;
    if (payload?.id && payload.id !== id && !payload?.created_by_match) {
      // Generic property events still warrant a stats refresh because
      // the user's "Active / Pending / Sold" counters may have shifted.
    }
    refresh();
  });

  if (authLoading) return <DashboardLayout title="User Profile" sidebar />;
  if (!user) return <div className="p-8 text-center">Please log in.</div>;
  if (!isAdmin) return <DashboardLayout title="User Profile" sidebar><div className="p-8 text-center" data-testid="user-profile-forbidden">Admins only.</div></DashboardLayout>;

  return (
    <DashboardLayout title="User Profile" sidebar>
      <div className="mb-4">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-[12px] text-[#52525B] hover:text-[#0055FF] transition" data-testid="user-profile-back">
            <ArrowLeft size={13} /> Back
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#A1A1AA]" data-testid="user-profile-loading">Loading profile…</div>
        ) : !data?.user ? (
          <div className="text-center py-12 text-[#A1A1AA]" data-testid="user-profile-not-found">User not found.</div>
        ) : (
          <>
            <ProfileHeader u={data.user} stats={data.stats} />

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
              <TabsList data-testid="user-profile-tabs">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="listings" data-testid="tab-listings">
                  Listings {data.stats?.total ? `(${data.stats.total})` : ""}
                </TabsTrigger>
                <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <OverviewPanel u={data.user} stats={data.stats} />
              </TabsContent>

              <TabsContent value="listings" className="mt-4">
                <ListingsPanel userId={id} onMutate={refresh} />
              </TabsContent>

              <TabsContent value="activity" className="mt-4">
                <ActivityPanel userId={id} />
              </TabsContent>
            </Tabs>
          </>
        )}
    </DashboardLayout>
  );
}

/* ─── Profile header (identity + stat cards) ───────────────────────── */

function ProfileHeader({ u, stats }) {
  const cards = [
    { label: "Total Listings",  value: stats?.total ?? 0,    color: "text-[#0F0F0F]", testid: "stat-total" },
    { label: "Active",          value: stats?.active ?? 0,   color: "text-[#15803D]", testid: "stat-active" },
    { label: "Pending",         value: stats?.pending ?? 0,  color: "text-[#92400E]", testid: "stat-pending" },
    { label: "Sold",            value: stats?.sold ?? 0,     color: "text-[#0055FF]", testid: "stat-sold" },
    { label: "Rejected",        value: stats?.rejected ?? 0, color: "text-[#DC2626]", testid: "stat-rejected" },
  ];

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm" data-testid="user-profile-header">
      <div className="p-5 flex items-start gap-4 border-b border-[#F4F4F5]">
        <div className="h-14 w-14 rounded-full bg-[#F4F4F5] inline-flex items-center justify-center text-[20px] font-bold text-[#27272A] flex-shrink-0">
          {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-[18px] font-bold text-[#09090B]" data-testid="user-profile-name">{u.name || "Unnamed"}</h1>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#EFF6FF] text-[#1D4ED8]" data-testid="user-profile-role">
              {u.role || "client"}
            </span>
            {u.blocked && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#FEE2E2] text-[#991B1B]">BLOCKED</span>
            )}
            {u.subscription?.tier && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#FFF7ED] text-[#9A3412] inline-flex items-center gap-1">
                <Shield size={10} /> {u.subscription.tier}
              </span>
            )}
          </div>
          <div className="text-[12px] text-[#52525B] mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1" data-testid="user-profile-email"><Mail size={11} /> {u.email || "—"}</span>
            <span className="inline-flex items-center gap-1" data-testid="user-profile-phone"><Phone size={11} /> {u.phone || "—"}</span>
            <span className="inline-flex items-center gap-1"><Calendar size={11} /> Joined {fmtDateShort(u.created_at)}</span>
            {u.last_login && (<span className="inline-flex items-center gap-1"><Clock size={11} /> Last login {fmtDateShort(u.last_login)}</span>)}
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-x divide-[#F4F4F5]" data-testid="user-profile-stats">
        {cards.map((c) => (
          <div key={c.label} className="p-4" data-testid={c.testid}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#71717A]">{c.label}</div>
            <div className={`text-[24px] font-bold leading-tight mt-1 ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Overview tab — personal info ─────────────────────────────────── */

function OverviewPanel({ u, stats }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <SectionCard title="Personal Information" testid="overview-personal">
        <DLRow label="Full name" value={u.name} />
        <DLRow label="Email"     value={u.email} />
        <DLRow label="Phone"     value={u.phone} />
        <DLRow label="Role"      value={u.role} />
        <DLRow label="Joined"    value={fmtDate(u.created_at)} />
        <DLRow label="Last login" value={fmtDate(u.last_login)} />
        <DLRow label="Bio"       value={u.bio || "—"} />
      </SectionCard>
      <SectionCard title="Activity Snapshot" testid="overview-activity">
        <DLRow label="Total Listings"   value={stats?.total ?? 0} />
        <DLRow label="Active Listings"  value={stats?.active ?? 0} />
        <DLRow label="Pending Listings" value={stats?.pending ?? 0} />
        <DLRow label="Sold Listings"    value={stats?.sold ?? 0} />
        <DLRow label="Rejected Listings" value={stats?.rejected ?? 0} />
        <DLRow label="Subscription"     value={u.subscription?.tier || "—"} />
        <DLRow label="Subscription expiry" value={u.subscription?.expires_at ? fmtDate(u.subscription.expires_at) : "—"} />
      </SectionCard>
    </div>
  );
}

function SectionCard({ title, children, testid }) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#F4F4F5] text-[11px] uppercase tracking-wider font-semibold text-[#52525B]">
        {title}
      </div>
      <dl className="px-4 py-3 space-y-2 text-[13px]">{children}</dl>
    </div>
  );
}

function DLRow({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="text-[11px] text-[#71717A] w-32 flex-shrink-0 uppercase tracking-wider font-semibold">{label}</dt>
      <dd className="text-[#27272A] font-medium break-words flex-1">{value || "—"}</dd>
    </div>
  );
}

/* ─── Listings tab ─────────────────────────────────────────────────── */

function ListingsPanel({ userId, onMutate }) {
  const [items, setItems] = useState([]);
  const [bucket, setBucket] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${userId}/properties?status=${bucket}&limit=300`);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [userId, bucket]);

  useEffect(() => { load(); }, [load]);

  // v2.80 — Live refresh of the per-user listing table on cross-session
  // property mutations.
  useDataSync(["properties"], () => { load(); });

  const approve = async (pid) => {
    try { await api.post(`/admin/properties/${pid}/approve`); toast.success("Approved"); await load(); onMutate?.(); }
    catch { toast.error("Approve failed"); }
  };
  const reject = async (pid) => {
    const reason = window.prompt("Reason for rejection? (optional)");
    try { await api.post(`/admin/properties/${pid}/reject`, reason ? { reason } : {}); toast.success("Rejected"); await load(); onMutate?.(); }
    catch { toast.error("Reject failed"); }
  };
  const remove = async (pid) => {
    if (!window.confirm("Delete this property permanently?")) return;
    try { await api.delete(`/properties/${pid}`); toast.success("Deleted"); await load(); onMutate?.(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm" data-testid="user-listings-panel">
      <div className="px-4 py-3 border-b border-[#F4F4F5] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5" data-testid="user-listings-buckets">
          {STATUS_BUCKETS.map((b) => (
            <button
              key={b.key}
              onClick={() => setBucket(b.key)}
              className={`h-8 px-3 rounded-sm text-[12px] font-semibold transition ${bucket === b.key ? "bg-[#0F0F0F] text-white" : "bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]"}`}
              data-testid={b.testid}
            >
              {b.label}
            </button>
          ))}
        </div>
        <button onClick={load} className="text-[12px] inline-flex items-center gap-1 text-[#52525B] hover:text-[#0055FF]" data-testid="user-listings-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#52525B]">
              <th className="p-3 font-semibold">Property ID</th>
              <th className="p-3 font-semibold">Property</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold text-right">Views</th>
              <th className="p-3 font-semibold text-right">Leads</th>
              <th className="p-3 font-semibold">Created</th>
              <th className="p-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-[#A1A1AA]" data-testid="user-listings-loading">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-[#A1A1AA]" data-testid="user-listings-empty">No {bucket === "all" ? "" : bucket + " "}listings for this user.</td></tr>
            )}
            {!loading && items.map((p) => (
              <tr key={p.id} className="border-b border-[#F4F4F5]" data-testid={`user-listing-row-${p.id}`}>
                <td className="p-3 font-mono-data text-[11px] text-[#52525B]" title={p.id}>{String(p.id).slice(0, 8)}…</td>
                <td className="p-3">
                  <Link to={`/property/${p.id}`} className="text-[13px] font-medium text-[#0055FF] hover:underline line-clamp-1" data-testid={`user-listing-title-${p.id}`}>
                    {p.title || "Untitled"}
                  </Link>
                  <div className="text-[11px] text-[#71717A] mt-0.5 flex items-center gap-2 flex-wrap">
                    {p.price ? <span className="font-semibold text-[#27272A] inline-flex items-center gap-0.5"><IndianRupee size={9} /> {formatINR(p.price).replace("₹", "")}</span> : null}
                    {(p.main_area || p.sub_area) && <span className="inline-flex items-center gap-0.5"><MapPin size={10} /> {p.sub_area || p.main_area}</span>}
                  </div>
                </td>
                <td className="p-3"><StatusBadge p={p} /></td>
                <td className="p-3 text-right text-[12px] font-semibold text-[#27272A]">{p.views || 0}</td>
                <td className="p-3 text-right text-[12px] font-semibold text-[#27272A]">{p.saved_count || 0}</td>
                <td className="p-3 text-[11.5px] text-[#52525B] whitespace-nowrap">{fmtDateShort(p.created_at)}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Link to={`/property/${p.id}`} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#F4F4F5] text-[#52525B]" title="View" data-testid={`user-listing-view-${p.id}`}><Eye size={13} /></Link>
                    <Link to={`/admin/listings?edit=${p.id}`} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#F4F4F5] text-[#52525B]" title="Edit" data-testid={`user-listing-edit-${p.id}`}><Pencil size={13} /></Link>
                    {p.approval_status === "pending" && (
                      <>
                        <button onClick={() => approve(p.id)} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#DCFCE7] text-[#15803D]" title="Approve" data-testid={`user-listing-approve-${p.id}`}><CheckCircle2 size={13} /></button>
                        <button onClick={() => reject(p.id)} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#FEE2E2] text-[#DC2626]" title="Reject" data-testid={`user-listing-reject-${p.id}`}><Ban size={13} /></button>
                      </>
                    )}
                    <button onClick={() => remove(p.id)} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#FEE2E2] text-[#DC2626]" title="Delete" data-testid={`user-listing-delete-${p.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ p }) {
  if (["Sold", "Rented"].includes(p.status)) {
    return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#DBEAFE] text-[#1D4ED8]" data-testid={`status-badge-${p.id}`}>SOLD</span>;
  }
  if (p.approval_status === "approved") return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#DCFCE7] text-[#15803D]" data-testid={`status-badge-${p.id}`}>ACTIVE</span>;
  if (p.approval_status === "pending") return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#FEF3C7] text-[#92400E]" data-testid={`status-badge-${p.id}`}>PENDING</span>;
  if (p.approval_status === "rejected") return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#FEE2E2] text-[#991B1B]" data-testid={`status-badge-${p.id}`}>REJECTED</span>;
  return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#F4F4F5] text-[#52525B]">{(p.approval_status || "DRAFT").toUpperCase()}</span>;
}

/* ─── Activity tab ─────────────────────────────────────────────────── */

function ActivityPanel({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/users/${userId}/activity?limit=200`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm" data-testid="user-activity-panel">
      <div className="px-4 py-3 border-b border-[#F4F4F5] text-[11px] uppercase tracking-wider font-semibold text-[#52525B] inline-flex items-center gap-1.5">
        <Activity size={11} /> Recent activity ({items.length})
      </div>
      <ul>
        {loading && <li className="p-8 text-center text-[#A1A1AA] text-[13px]" data-testid="user-activity-loading">Loading…</li>}
        {!loading && items.length === 0 && (<li className="p-8 text-center text-[#A1A1AA] text-[13px]" data-testid="user-activity-empty">No activity recorded yet.</li>)}
        {!loading && items.map((a, i) => (
          <li key={a.id || i} className="px-4 py-2.5 border-b border-[#F4F4F5] flex items-center gap-3 text-[12.5px]" data-testid={`user-activity-row-${i}`}>
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#71717A] w-28 flex-shrink-0">{a.kind || a.event || "event"}</span>
            <span className="flex-1 truncate text-[#27272A]">
              {a.meta?.title || a.ref_id || "—"}
            </span>
            <span className="text-[11px] text-[#71717A] whitespace-nowrap">{fmtDateShort(a.created_at)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
